import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from '../components/SEO';
import { getAppCheckToken } from '../firebase/firebase-config';
import '../Styles/ContractCompass.css';

const MAX_FILE_BYTES = 8 * 1024 * 1024;

const DATE_CATEGORY_META = {
  start: 'Start',
  end: 'End',
  payment: 'Payment',
  notice: 'Notice',
  renewal: 'Renewal',
  milestone: 'Milestone',
  other: 'Date',
};

const SCORECARD_LABELS = {
  dates: 'Dates',
  payments: 'Payments',
  responsibilities: 'Responsibilities',
  exit: 'Exit process',
  renewal: 'Renewal',
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function PageBadge({ page }) {
  return page ? <span className="cc-page-badge">p. {page}</span> : null;
}

function pad2(n) { return String(n).padStart(2, '0'); }

function icsEscape(s) {
  return String(s || '').replace(/[\\;,]/g, m => `\\${m}`).replace(/\n/g, '\\n');
}

function buildICS(dates, sourceName) {
  const now = new Date();
  const stamp = `${now.getUTCFullYear()}${pad2(now.getUTCMonth() + 1)}${pad2(now.getUTCDate())}T${pad2(now.getUTCHours())}${pad2(now.getUTCMinutes())}${pad2(now.getUTCSeconds())}Z`;
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//The PP Studio//Contract Compass//EN', 'CALSCALE:GREGORIAN'];
  dates.forEach((d, i) => {
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:cc-${stamp}-${i}@pp-studio-website.web.app`);
    lines.push(`DTSTAMP:${stamp}`);
    lines.push(`DTSTART;VALUE=DATE:${d.date_iso.replace(/-/g, '')}`);
    lines.push(`SUMMARY:${icsEscape(d.label)}${sourceName ? ` (${icsEscape(sourceName)})` : ''}`);
    if (d.detail) lines.push(`DESCRIPTION:${icsEscape(d.detail)}`);
    lines.push('END:VEVENT');
  });
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

function downloadBlob(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const findingLine = (f) => f?.page ? `${f.text} (p. ${f.page})` : (f?.text || '');

function buildChecklistText(analysis, sourceName) {
  const lines = [`CONTRACT BRIEF${sourceName ? ` — ${sourceName}` : ''}`, 'Prepared by PP Contract Compass — not legal advice.'];
  const brief = analysis.deal_brief || {};
  ['parties', 'purpose', 'value', 'duration'].forEach(k => { if (brief[k]) lines.push(`${cap(k)}: ${brief[k]}`); });

  const section = (title, items) => {
    if (!items.length) return;
    lines.push('', title.toUpperCase());
    items.forEach(t => lines.push(`- ${t}`));
  };
  section('Key obligations', (analysis.deliverables || []).map(findingLine));
  section('Money and payment timing', (analysis.payment_schedule || []).map(p =>
    [p.description, p.amount, p.due].filter(Boolean).join(' — ') + (p.page ? ` (p. ${p.page})` : '')));
  section('Dates to keep in view', (analysis.key_dates || []).map(d =>
    `${d.label}${d.detail ? ` — ${d.detail}` : ''}${d.date_iso ? ` (${d.date_iso})` : ''}`));
  section('What you need from the other side', (analysis.client_obligations || []).map(findingLine));
  section('Pause and look again', (analysis.attention_flags || []).map(findingLine));
  section('Questions worth asking', analysis.questions_to_raise || []);
  return lines.join('\n');
}

export default function ContractCompass() {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | analyzing | done | error
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copyStatus, setCopyStatus] = useState('idle'); // idle | copied
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle'); // idle | sending | sent | error
  const [emailError, setEmailError] = useState('');
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) { setError('Please upload a PDF file.'); return; }
    if (f.size > MAX_FILE_BYTES) { setError('That PDF is larger than 8MB — please upload a smaller file.'); return; }
    setError('');
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  };

  const analyze = async () => {
    if (!file) return;
    setStatus('analyzing');
    setError('');
    try {
      const fileBase64 = await fileToBase64(file);
      const appCheckToken = await getAppCheckToken();
      const res = await fetch('/api/contract-compass/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(appCheckToken ? { 'X-Firebase-AppCheck': appCheckToken } : {}),
        },
        body: JSON.stringify({ filename: file.name, fileBase64 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Something went wrong reading that contract.');
      setResult(data);
      setStatus('done');
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again.');
      setStatus('error');
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setStatus('idle');
    setError('');
    setEmailOpen(false);
    setEmailAddr('');
    setEmailStatus('idle');
    if (inputRef.current) inputRef.current.value = '';
  };

  const analysis = result?.analysis;
  const hasAnyOf = (arr) => Array.isArray(arr) && arr.length > 0;

  const datedTimeline = (analysis?.key_dates || [])
    .filter(d => d.date_iso)
    .slice()
    .sort((a, b) => a.date_iso.localeCompare(b.date_iso));
  const undatedTimeline = (analysis?.key_dates || []).filter(d => !d.date_iso);

  const copyChecklist = async () => {
    if (!analysis) return;
    try {
      await navigator.clipboard.writeText(buildChecklistText(analysis, file?.name));
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2200);
    } catch {
      setCopyStatus('idle');
    }
  };

  const downloadICS = () => {
    if (!datedTimeline.length) return;
    downloadBlob('contract-dates.ics', buildICS(datedTimeline, file?.name), 'text/calendar');
  };

  const sendBriefEmail = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddr.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailStatus('sending');
    setEmailError('');
    try {
      const appCheckToken = await getAppCheckToken();
      const res = await fetch('/api/contract-compass/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(appCheckToken ? { 'X-Firebase-AppCheck': appCheckToken } : {}),
        },
        body: JSON.stringify({ email: emailAddr.trim(), analysis, filename: file?.name, pageCount: result?.pageCount }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not send — please try again.');
      setEmailStatus('sent');
    } catch (err) {
      setEmailStatus('error');
      setEmailError(err.message || 'Could not send — please try again.');
    }
  };

  const UploadDesk = () => (
    <div className="cc-upload-desk">
      <div className="cc-desk-heading">
        <span className="cc-desk-number">01</span>
        <div>
          <strong>Start with your agreement</strong>
          <span>Signed or still in draft</span>
        </div>
      </div>

      <div
        className={`cc-dropzone${dragOver ? ' over' : ''}${file ? ' has-file' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={file ? `Selected file: ${file.name}. Choose a different PDF` : 'Choose a contract PDF'}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          onChange={e => pickFile(e.target.files?.[0])}
          hidden
        />
        <span className="cc-file-mark" aria-hidden="true"><i>PDF</i></span>
        {file ? (
          <div className="cc-file-copy">
            <strong>{file.name}</strong>
            <span>{(file.size / (1024 * 1024)).toFixed(1)} MB · Ready to read</span>
            <small>Choose a different file</small>
          </div>
        ) : (
          <div className="cc-file-copy">
            <strong>Drop your contract here</strong>
            <span>or choose a PDF from your device</span>
            <small>PDF · Maximum 8 MB</small>
          </div>
        )}
      </div>

      {error && <p className="cc-error" role="alert">{error}</p>}

      <button
        type="button"
        className="cc-analyze-btn"
        disabled={!file || status === 'analyzing'}
        onClick={analyze}
      >
        {status === 'analyzing' ? (
          <><span className="cc-btn-spinner" aria-hidden="true" />Building your contract brief…</>
        ) : (
          <>Create my contract brief <span aria-hidden="true">→</span></>
        )}
      </button>

      <div className="cc-privacy-note">
        <span className="cc-lock" aria-hidden="true">⌁</span>
        <p><strong>Your document stays private.</strong> PP Studio does not save a copy. Its text is sent securely to our AI processor for this analysis and discarded from request memory afterwards. <Link to="/privacy">Privacy details</Link></p>
      </div>
    </div>
  );

  return (
    <>
      <SEO
        title="Contract Compass — Turn a contract into a checklist | The PP Studio"
        description="Upload a contract and get an instant operational breakdown — deliverables, payment schedule, key dates, and what to raise with Pooja. Not legal advice."
        url="https://pp-studio-website.web.app/contract-compass"
      />
      <Navbar />

      <main className="cc-main">
        {status !== 'done' && <section className="cc-hero">
          <div className="cc-hero-inner">
            <div className="cc-hero-copy">
              <span className="cc-kicker"><i /> PP Contract Compass · AI-Powered</span>
              <h1 className="cc-title">Your contract,<br /><em>made workable.</em></h1>
              <p className="cc-subtitle">
                Use this simple checklist to work through an agreement and identify the
                commercial details that matter: what is due, who is responsible, when
                payments should move, and which points deserve a closer conversation.
              </p>
              <div className="cc-outcomes" aria-label="What your brief includes">
                <div><span>01</span><p><strong>Deal overview</strong>Parties, purpose and term</p></div>
                <div><span>02</span><p><strong>Key obligations</strong>Deliverables and responsibilities</p></div>
                <div><span>03</span><p><strong>Points to clarify</strong>Dates, payments and questions</p></div>
              </div>
              <blockquote className="cc-pooja-note">
                <p>“A contract becomes useful when you can see what it asks of you—not only what it says.”</p>
                <cite>Pooja Biswal · Contract Specialist</cite>
              </blockquote>
            </div>
            <UploadDesk />
          </div>
        </section>}

        {status === 'done' && <section className="cc-body cc-body-results">
          <div className="cc-body-inner">
            {status === 'done' && analysis && (
              <div className="cc-results" id="cc-print-area">
                <div className="cc-results-kicker"><span>PP</span> Contract Compass · AI Working Brief</div>
                <div className="cc-results-head">
                  <div>
                    <span>Prepared from {file?.name || 'your contract'}{result.pageCount ? ` · ${result.pageCount} pages` : ''}</span>
                    <h1>Your contract brief</h1>
                    <p>A practical reading of the document—not a substitute for professional legal advice.</p>
                  </div>
                  <button type="button" className="cc-reset-btn cc-no-print" onClick={reset}>New document <span>↗</span></button>
                </div>

                <div className="cc-export-row cc-no-print">
                  <button type="button" className="cc-export-btn" onClick={() => window.print()}>Download PDF</button>
                  <button type="button" className="cc-export-btn" onClick={copyChecklist}>
                    {copyStatus === 'copied' ? 'Copied ✓' : 'Copy checklist'}
                  </button>
                  <button type="button" className="cc-export-btn" onClick={() => setEmailOpen(o => !o)}>Email this brief</button>
                </div>

                {emailOpen && (
                  <form className="cc-email-form cc-no-print" onSubmit={sendBriefEmail}>
                    {emailStatus === 'sent' ? (
                      <p className="cc-email-sent">Sent to {emailAddr} — check your inbox.</p>
                    ) : (
                      <>
                        <input
                          type="email"
                          placeholder="you@example.com"
                          value={emailAddr}
                          onChange={e => setEmailAddr(e.target.value)}
                        />
                        <button type="submit" disabled={emailStatus === 'sending'}>
                          {emailStatus === 'sending' ? 'Sending…' : 'Send'}
                        </button>
                      </>
                    )}
                    {emailError && <p className="cc-error" style={{ flexBasis: '100%' }}>{emailError}</p>}
                  </form>
                )}

                <div className="cc-scorecard cc-no-print">
                  <span className="cc-scorecard-label">Compass Scorecard — operational completeness</span>
                  <div className="cc-scorecard-grid">
                    {Object.entries(SCORECARD_LABELS).map(([key, label]) => (
                      <div className={`cc-score-pill ${analysis.scorecard?.[key] || 'incomplete'}`} key={key}>
                        <span className="cc-score-dot" aria-hidden="true" />
                        <span>{label}</span>
                        <small>{analysis.scorecard?.[key] === 'clear' ? 'Clear' : 'Incomplete'}</small>
                      </div>
                    ))}
                  </div>
                </div>

                {result.truncated && (
                  <p className="cc-notice">This document was long, so the breakdown covers the first portion of it.</p>
                )}

                <div className="cc-card cc-brief">
                  <div className="cc-section-label"><span>01</span><h2>The agreement at a glance</h2></div>
                  <dl>
                    {analysis.deal_brief?.parties && <><dt>Parties</dt><dd>{analysis.deal_brief.parties}</dd></>}
                    {analysis.deal_brief?.purpose && <><dt>Purpose</dt><dd>{analysis.deal_brief.purpose}</dd></>}
                    {analysis.deal_brief?.value && <><dt>Value</dt><dd>{analysis.deal_brief.value}</dd></>}
                    {analysis.deal_brief?.duration && <><dt>Duration</dt><dd>{analysis.deal_brief.duration}</dd></>}
                  </dl>
                </div>

                {hasAnyOf(analysis.deliverables) && (
                  <div className="cc-card">
                    <div className="cc-section-label"><span>02</span><h2>Your working checklist</h2></div>
                    <ul className="cc-checklist">
                      {analysis.deliverables.map((d, i) => (
                        <li key={i}><span className="cc-check">✓</span><span>{d.text}<PageBadge page={d.page} /></span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {hasAnyOf(analysis.payment_schedule) && (
                  <div className="cc-card">
                    <div className="cc-section-label"><span>03</span><h2>Money and payment timing</h2></div>
                    <div className="cc-payments">
                      {analysis.payment_schedule.map((p, i) => (
                        <div className="cc-payment-row" key={i}>
                          <span className="cc-payment-desc">{p.description}<PageBadge page={p.page} /></span>
                          {p.amount && <span className="cc-payment-amount">{p.amount}</span>}
                          {p.due && <span className="cc-payment-due">{p.due}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {hasAnyOf(analysis.key_dates) && (
                  <div className="cc-card">
                    <div className="cc-section-label">
                      <span>04</span><h2>Contract timeline</h2>
                      {datedTimeline.length > 0 && (
                        <button type="button" className="cc-ics-btn cc-no-print" onClick={downloadICS}>
                          Download calendar (.ics)
                        </button>
                      )}
                    </div>
                    {datedTimeline.length > 0 && (
                      <ul className="cc-timeline">
                        {datedTimeline.map((d, i) => (
                          <li key={i}>
                            <span className="cc-timeline-date">{d.date_iso}</span>
                            <span className="cc-timeline-cat">{DATE_CATEGORY_META[d.category] || 'Date'}</span>
                            <span className="cc-timeline-body"><strong>{d.label}</strong>{d.detail && <span> — {d.detail}</span>}<PageBadge page={d.page} /></span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {undatedTimeline.length > 0 && (
                      <>
                        <p className="cc-section-intro" style={{ margin: datedTimeline.length ? '1.1rem 0 .6rem 0' : '0 0 .6rem 0' }}>
                          Also mentioned — no fixed calendar date given in the document:
                        </p>
                        <ul className="cc-dates">
                          {undatedTimeline.map((d, i) => (
                            <li key={i}>
                              <span className="cc-timeline-cat inline">{DATE_CATEGORY_META[d.category] || 'Date'}</span>{' '}
                              <strong>{d.label}</strong>{d.detail && <span> — {d.detail}</span>}<PageBadge page={d.page} />
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}

                {hasAnyOf(analysis.client_obligations) && (
                  <div className="cc-card">
                    <div className="cc-section-label"><span>05</span><h2>What you need from the other side</h2></div>
                    <ul className="cc-checklist">
                      {analysis.client_obligations.map((o, i) => (
                        <li key={i}><span className="cc-check">○</span><span>{o.text}<PageBadge page={o.page} /></span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {hasAnyOf(analysis.attention_flags) && (
                  <div className="cc-card cc-flags">
                    <div className="cc-section-label"><span>!</span><h2>Pause and look again</h2></div>
                    <p className="cc-section-intro">These points may have practical consequences. Compass is flagging the wording—not deciding what you should do.</p>
                    <ul>
                      {analysis.attention_flags.map((f, i) => <li key={i}>{f.text}<PageBadge page={f.page} /></li>)}
                    </ul>
                  </div>
                )}

                {hasAnyOf(analysis.questions_to_raise) && (
                  <div className="cc-card">
                    <div className="cc-section-label"><span>?</span><h2>Questions worth asking</h2></div>
                    <ul>
                      {analysis.questions_to_raise.map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                  </div>
                )}

                <div className="cc-card cc-desk-upsell cc-no-print">
                  <span className="cc-desk-eyebrow">Want Compass to remember this?</span>
                  <h2>This brief disappears when you close the tab.</h2>
                  <p>Contract Desk keeps every contract like this one in one place — tracking deadlines, payments, and obligations across all your agreements, not just this one.</p>
                  <Link to="/desk/signup" className="cc-desk-btn">Set up my Contract Desk →</Link>
                </div>

                <div className="cc-cta-card cc-no-print">
                  <span className="cc-cta-eyebrow">When the document needs human judgment</span>
                  <h2>Bring Pooja into the conversation.</h2>
                  <p>Compass helps you find the questions. A professional review helps you decide what to do with them.</p>
                  <div className="cc-cta-row">
                    <Link to="/work-with-me" className="cc-cta-primary">Discuss this contract →</Link>
                    <Link to="/contract-tips" className="cc-cta-secondary">Explore contract notes</Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>}
      </main>

      <Footer />
    </>
  );
}
